import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, Checkbox, Flex, Grid, Text, Tooltip, useToast } from '@chakra-ui/react';
import ModalComponent from '../Modal';
import { getRegionStates, associateRegionStates } from '../../redux/action/client.action';
import { CUSTOM_TOAST_DURATION_NULL, TOAST_SETTINGS } from '../../constants';

const validationSchema = Yup.object().shape({
  selectedStates: Yup.array().min(1, 'At least one state must be selected'),
  selectAll: Yup.boolean(),
});

const StateSelectionModal = ({ isOpen, onClose, clientId, onRefreshParent }) => {
  const [states, setStates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTooltips, setShowTooltips] = useState({});
  const textRefs = useRef({});
  const toast = useToast();

  const formik = useFormik({
    initialValues: {
      selectedStates: [],
      selectAll: false,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);
        const selectedStateIds = states
          .filter((_, index) => values.selectedStates.includes(index))
          .map((state) => state.id);

        // Call API to associate region states
        const payload = { tax_region_ids: selectedStateIds };
        const result = await associateRegionStates(clientId, payload);

        if (result.ok) {
          // Refresh parent component with updated client states
          toast({
            ...TOAST_SETTINGS.SETTINGS,
            duration: CUSTOM_TOAST_DURATION_NULL.duration,
            status: TOAST_SETTINGS.SUCCESS,
            title: result.message || 'Region states associated successfully',
          });
          if (onRefreshParent) {
            await onRefreshParent();
          }
        } else {
          toast({
            ...TOAST_SETTINGS.SETTINGS,
            duration: CUSTOM_TOAST_DURATION_NULL.duration,
            status: TOAST_SETTINGS.ERROR,
            title: result.error || 'Failed to associate region states',
          });
        }
      } catch (error) {
        console.error('Error associating region states:', error);
        toast({
          ...TOAST_SETTINGS.SETTINGS,
          duration: CUSTOM_TOAST_DURATION_NULL.duration,
          status: TOAST_SETTINGS.ERROR,
          title: 'Failed to associate region states',
        });
      } finally {
        setIsSubmitting(false);
        onClose();
        resetForm();
      }
    },
  });

  const { values, setFieldValue, handleSubmit, resetForm } = formik;

  useEffect(() => {
    // Check text overflow for each state
    states.forEach((state, index) => {
      const element = textRefs.current[index];
      if (element) {
        setShowTooltips((prev) => ({
          ...prev,
          [index]: element.scrollWidth > element.clientWidth,
        }));
      }
    });
  }, [states]);

  const fetchStates = async () => {
    setIsLoading(true);
    try {
      // Fetch first page to get total pages
      const firstPageResult = await getRegionStates(clientId, 50, 1);

      if (!firstPageResult.ok || !firstPageResult.data) {
        setStates([]);
        setIsLoading(false);
        return;
      }

      const totalPages = firstPageResult.data.pagination?.total_pages || 1;
      const firstPageStates = firstPageResult.data.tax_regions.map((state) => ({
        name: state.name,
        id: state.id,
        isSelected: state.is_selected || false,
      }));

      // If only one page, use first page data
      if (totalPages === 1) {
        setStates(firstPageStates);
        const selectedIndices = firstPageStates
          .map((state, index) => (state.isSelected ? index : -1))
          .filter((index) => index !== -1);
        setFieldValue('selectedStates', selectedIndices);
        setFieldValue('selectAll', selectedIndices.length === firstPageStates.length);
        setIsLoading(false);
        return;
      }

      // Fetch remaining pages in parallel
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const remainingResults = await Promise.all(
        remainingPages.map((page) => getRegionStates(clientId, 50, page))
      );

      // Combine all results
      let allStates = [...firstPageStates];
      remainingResults.forEach((result) => {
        if (result.ok && result.data) {
          const fetchedStates = result.data.tax_regions.map((state) => ({
            name: state.name,
            id: state.id,
            isSelected: state.is_selected || false,
          }));
          allStates = [...allStates, ...fetchedStates];
        }
      });

      setStates(allStates);

      // Initialize form with selected states
      const selectedIndices = allStates
        .map((state, index) => (state.isSelected ? index : -1))
        .filter((index) => index !== -1);

      setFieldValue('selectedStates', selectedIndices);
      setFieldValue('selectAll', selectedIndices.length === allStates.length);
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    }
    setIsLoading(false);
  };
  useEffect(() => {
    if (isOpen) {
      fetchStates();
    }
  }, [isOpen]);
  const handleSelectAllToggle = (checked) => {
    setFieldValue('selectAll', checked);
    if (checked) {
      setFieldValue(
        'selectedStates',
        states.map((_, index) => index)
      );
    } else {
      setFieldValue('selectedStates', []);
    }
  };

  const handleStateToggle = (index) => {
    const newSelectedStates = values.selectedStates.includes(index)
      ? values.selectedStates.filter((i) => i !== index)
      : [...values.selectedStates, index];

    setFieldValue('selectedStates', newSelectedStates);
    setFieldValue('selectAll', newSelectedStates.length === states.length);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" minH="200px">
          <Text fontSize="lg" color="gray.500">
            Loading states...
          </Text>
        </Flex>
      );
    }

    if (!states.length) {
      return (
        <Flex
          direction="column"
          justify="center"
          align="center"
          minH="200px"
          p={6}
          bg="gray.50"
          borderRadius="md"
        >
          <Text fontSize="xl" fontWeight="medium" color="gray.600" mb={2}>
            No States Available
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="center">
            There are currently no states available for selection.
          </Text>
        </Flex>
      );
    }

    return (
      <>
        <Flex
          bg="gray.50"
          p={4}
          borderRadius="md"
          mb={4}
          alignItems="center"
          justifyContent="space-between"
        >
          <Checkbox
            size="lg"
            isChecked={values.selectAll}
            onChange={(e) => handleSelectAllToggle(e.target.checked)}
            colorScheme="blue"
          >
            <Text fontWeight="medium">Select All States</Text>
          </Checkbox>
          <Text color="gray.600" fontSize="sm">
            Selected: {values.selectedStates.length} / {states.length}
          </Text>
        </Flex>

        <Box
          paddingTop={4}
          maxH="500px"
          overflowY="auto"
          sx={{
            '&::-webkit-scrollbar': {
              width: '8px',
              borderRadius: '8px',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
              },
            },
          }}
        >
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            {states.map((state, index) => (
              <Flex
                key={state.id}
                p={4}
                bg="white"
                borderRadius="md"
                border="1px"
                borderColor={values.selectedStates.includes(index) ? 'main.primary' : 'gray.200'}
                _hover={{
                  bg: 'gray.50',
                  borderColor: 'main.primary',
                  transform: 'translateY(-1px)',
                }}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => handleStateToggle(index)}
                minH="60px"
                minW="200px"
                alignItems="center"
              >
                <Tooltip
                  label={state.name}
                  placement="top"
                  hasArrow
                  isDisabled={!showTooltips[index]}
                >
                  <Text
                    ref={(el) => {
                      textRefs.current[index] = el;
                    }}
                    noOfLines={1}
                    fontSize="15px"
                    width="320px"
                    cursor="pointer"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {state.name}
                  </Text>
                </Tooltip>
                <Checkbox
                  size="lg"
                  isChecked={values.selectedStates.includes(index)}
                  colorScheme="blue"
                  pointerEvents="none"
                  ml={2}
                />
              </Flex>
            ))}
          </Grid>
        </Box>
      </>
    );
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      specifySize="4xl"
      onClose={() => {
        onClose();
        resetForm();
      }}
      modalTitle="State Selection"
      modalContent={<Box p={6}>{renderContent()}</Box>}
      negative={
        <Button
          bg="main.secondary"
          mr={3}
          _hover={{ bg: 'main.secondary' }}
          color="main.black"
          onClick={() => {
            onClose();
            resetForm();
          }}
        >
          Cancel
        </Button>
      }
      positive={
        <Button
          bg="main.semiPrimary"
          color="main.white"
          _hover={{ bg: 'main.primary' }}
          _active={{
            bg: 'main.semiPrimary',
            borderColor: 'main.primary',
          }}
          onClick={handleSubmit}
          isDisabled={!states.length || values.selectedStates.length === 0 || isSubmitting}
          isLoading={isSubmitting}
        >
          Selected States ({values.selectedStates.length})
        </Button>
      }
    />
  );
};

export default StateSelectionModal;
